import { GetMyBusinessesResponse } from "@1games-ru/region-shared/dist/backend/requests/business";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import BusinessMpProvider from "@/core/data-provider/data-mp/business/business-mp-provider";
import GameMpProvider from "@/core/data-provider/data-mp/game/game-mp-provider";
import useAppDispatch from "@/core/helpers/useAppDispatch";
import useAppSelector from "@/core/helpers/useAppSelector";
import { RpcMpProviderFailedResponse } from "@/core/rpc-mp-provider/responses/rpc-mp-provider-failed-response";
import { infoSelector, setInfo } from "@/domains/business/slice";
import { clearOffer } from "@/domains/offer/slice";
import toast from "@/domains/toasts/helpers/toast";

export default function useOfferCreatorBusiness(isShow: boolean) {
  const { t, i18n } = useTranslation("offer");
  const dispatch = useAppDispatch();

  const [business, setBusiness] = useState<GetMyBusinessesResponse>([]);
  const [pending, setPending] = useState(false);
  const [loading, setLoading] = useState(false);

  const info = useAppSelector(infoSelector);

  const close = useCallback(() => {
    dispatch(clearOffer());
    GameMpProvider.closeCurrentInterface(true);
  }, [dispatch]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const response = await BusinessMpProvider.getMyBusinesses();

      if (response instanceof RpcMpProviderFailedResponse) {
        if (i18n.exists(`offer:offer.errors.${response.error.message}`)) {
          console.error(response.error.message);
          toast.error(t(`offer.errors.${response.error.message}`));
        } else {
          console.error(response.error.message);
          toast.error(t("offer.toast.loadError"));
        }
        return;
      }

      setBusiness(response.data);
    } catch (e) {
      console.error(e);
      toast.error(t("offer.toast.loadError"));
    } finally {
      setLoading(false);
    }
  }, [i18n, t]);

  const create = useCallback(
    async (businessId: number, characterId: number, estatePrice: number) => {
      setPending(true);
      try {
        const response = await BusinessMpProvider.tradeBusiness(
          businessId,
          characterId,
          estatePrice,
        );

        if (response instanceof RpcMpProviderFailedResponse) {
          if (i18n.exists(`offer:offer.errors.${response.error.message}`)) {
            console.error(response.error.message);
            toast.error(t(`offer.errors.${response.error.message}`));
          } else {
            console.error(response.error.message);
            toast.error(t("offer.toast.createError"));
          }
          return false;
        }

        if (businessId === info?.id) dispatch(setInfo(null));
        toast.success(t("offer.toast.accepted"));
        close();
        return true;
      } catch (e) {
        console.error(e);
        toast.error(t("offer.toast.createError"));
        return false;
      } finally {
        setPending(false);
      }
    },
    [info?.id, dispatch, t, close, i18n],
  );

  useEffect(() => {
    if (isShow) {
      load();
    } else {
      setBusiness([]);
    }
  }, [isShow, load]);

  return {
    business,
    create,
    close,
    pending,
    loading,
  };
}
